/**
 * ArtifactsScreen — Artefatos técnicos (Fase 3 · Bloco 2).
 *
 * "Gerar artefatos com IA" → POST .../artifacts/generate (backend/cloud-architect
 * via Claude) gera ADRs/C4/Topologia/Terraform reais (markdown) e persiste. Lista
 * como accordions expansíveis. Lê ?project= (fallback: projeto mais recente).
 */
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useArtifactCatalog, useArtifacts, useGenerateArtifacts } from '@/api/techScope';
import { RichContent, splitMermaid, MermaidBlock } from '@/components/MermaidView';
import { AiTruncationNotice } from '@/components/ui';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).
const KIND = {
  adr:        { label: 'ADR', fg: C.accent },
  c4:         { label: 'C4', fg: C.quantum },
  sequencia:  { label: 'Sequência', fg: C.cyber },
  topologia:  { label: 'Topologia', fg: C.quantum },
  capacidade: { label: 'Capacidade', fg: C.dim },
  terraform:  { label: 'Terraform', fg: C.digital },
  roadmap:    { label: 'Roadmap', fg: C.dim },
  doc:        { label: 'Doc', fg: C.muted },
};

// O CATÁLOGO VEM DA API (`useArtifactCatalog` → GET /artifacts/catalog).
//
// Itens 34/N32 da auditoria: esta tela tinha um catálogo próprio, hardcoded, de
// 15 rótulos em texto livre ("C4 · Nível 1 (Contexto)") — e mandava o RÓTULO ao
// gerar. O backend resolve o contrato pelo catálogo canônico, onde 10 desses 15
// rótulos não casavam padrão nenhum, os três de C4 inclusive: a seleção do
// usuário ia para o lixo em silêncio e o piso mínimo do backend salvava o
// resultado por acidente. Marcar ou desmarcar C4 aqui nunca teve efeito. De
// quebra, o contador dizia "/13" com 15 itens na lista.
//
// Agora a tela lê o catálogo canônico e envia a CHAVE (`c4-componente`), e um
// artefato novo no catálogo aparece aqui sem deploy do front.
//
// Fallback local: usado só enquanto a chamada não volta (ou se ela falhar), para
// a tela não aparecer vazia. São as MESMAS chaves canônicas do backend.
const CATALOGO_FALLBACK = [
  { chave: 'c4-contexto',   titulo: 'C4 Nível 1 — Diagrama de Contexto',    kind: 'c4',        obrigatorio: true },
  { chave: 'c4-container',  titulo: 'C4 Nível 2 — Diagrama de Contêineres', kind: 'c4',        obrigatorio: true },
  { chave: 'c4-componente', titulo: 'C4 Nível 3 — Diagrama de Componentes', kind: 'c4',        obrigatorio: true },
  { chave: 'adr',           titulo: 'ADRs — Decisões de Arquitetura',       kind: 'adr',       obrigatorio: true },
  { chave: 'seq',           titulo: 'Diagramas de Sequência',               kind: 'sequencia', obrigatorio: true },
  { chave: 'topologia',     titulo: 'Topologia / Arquitetura Cloud',        kind: 'topologia', obrigatorio: true },
  { chave: 'terraform-iac', titulo: 'Infraestrutura como Código (Terraform)', kind: 'terraform', obrigatorio: true },
];

// Rótulo pt-BR do agrupamento por `kind` — o catálogo já traz o kind, então a
// tela não precisa inventar uma segunda taxonomia de grupos.
const GRUPO_POR_KIND = {
  c4:         'Diagramas C4',
  adr:        'Decisões',
  sequencia:  'Sequência',
  topologia:  'Topologia',
  terraform:  'Materialização',
  roadmap:    'Materialização',
  capacidade: 'Capacidades',
  doc:        'Documentação técnica',
};

/** Agrupa o catálogo por `kind` preservando a ordem canônica. */
function agruparPorKind(itens) {
  const grupos = [];
  const porNome = new Map();
  for (const item of itens) {
    const nome = GRUPO_POR_KIND[item.kind] || 'Outros';
    if (!porNome.has(nome)) {
      const g = { group: nome, items: [] };
      porNome.set(nome, g);
      grupos.push(g);
    }
    porNome.get(nome).items.push(item);
  }
  return grupos;
}

// Kinds que SÃO um diagrama: renderiza só o bloco mermaid (a IA às vezes anexa
// prosa fora do fence, que aparecia como "texto solto" abaixo do diagrama).
const DIAGRAM_KINDS = new Set(['c4', 'sequencia', 'topologia']);

function Artifact({ a }) {
  const [open, setOpen] = useState(false);
  const k = KIND[a.kind] || KIND.doc;
  const mermaidOnly = DIAGRAM_KINDS.has(a.kind)
    ? (splitMermaid(a.content).find((p) => p.type === 'mermaid')?.value || null)
    : null;
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, marginTop: 10, overflow: 'hidden' }}>
      <div onClick={() => setOpen(!open)} style={{ cursor: 'pointer', padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: C.accent, fontFamily: 'monospace' }}>{open ? '–' : '+'}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 8px', borderRadius: 6,
          background: C.surface2, color: k.fg }}>{k.label}</span>
        <strong style={{ fontSize: 13.5 }}>{a.title}</strong>
      </div>
      {open && (
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, background: C.black }}>
          {mermaidOnly ? <MermaidBlock code={mermaidOnly} /> : <RichContent content={a.content} />}
        </div>
      )}
    </div>
  );
}

export function ArtifactsScreen({ projectId: wizardProjectId } = {}) {
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = wizardProjectId || urlProject || projects?.[0]?.id || null;
  const { data, isLoading } = useArtifacts(projectId);
  const { data: catalogoRaw } = useArtifactCatalog();
  const generate = useGenerateArtifacts(projectId);
  const catalogo = useMemo(
    () => (Array.isArray(catalogoRaw?.items) && catalogoRaw.items.length
      ? catalogoRaw.items
      : CATALOGO_FALLBACK),
    [catalogoRaw],
  );
  const grupos = useMemo(() => agruparPorKind(catalogo), [catalogo]);
  // Desmarcados explicitamente pelo usuário. Guardar a EXCLUSÃO (e não a
  // seleção) evita o piscar de "nada selecionado" enquanto o catálogo carrega.
  const [desmarcados, setDesmarcados] = useState(() => new Set());
  const selected = useMemo(
    () => new Set(catalogo.filter((i) => !desmarcados.has(i.chave)).map((i) => i.chave)),
    [catalogo, desmarcados],
  );

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;
  const artifacts = data?.artifacts ?? [];
  const toggle = (chave, obrigatorio) => {
    if (obrigatorio) return;
    setDesmarcados((prev) => {
      const n = new Set(prev);
      n.has(chave) ? n.delete(chave) : n.add(chave);
      return n;
    });
  };
  // Envia a CHAVE CANÔNICA — era aqui que o rótulo em texto livre saía.
  const doGenerate = () => generate.mutate([...selected]);

  // Baixa TODOS os artefatos num único .md (título + tipo + conteúdo de cada um).
  const projName = projects.find((p) => p.id === projectId)?.name || 'Projeto';
  const downloadAllMd = () => {
    if (!artifacts.length) return;
    const md = [`# Artefatos técnicos — ${projName}`,
      `_${artifacts.length} artefatos · exportado do Nava Flow_`, '', '---', '']
      .concat(artifacts.map((a) => `## ${a.title}  \`[${a.kind || 'doc'}]\`\n\n${a.content || ''}\n`))
      .join('\n');
    const url = URL.createObjectURL(new Blob([md], { type: 'text/markdown;charset=utf-8' }));
    const el = document.createElement('a');
    el.href = url;
    el.download = `artefatos-tecnicos-${projName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.md`;
    document.body.appendChild(el); el.click(); el.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div className="sp-page" style={{ color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Artefatos técnicos</h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.dim }}>{selected.size}/{catalogo.length} selecionados</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {artifacts.length > 0 && (
            <button onClick={downloadAllMd}
              title="Baixa todos os artefatos num único arquivo .md"
              style={{ cursor: 'pointer', background: 'transparent', color: C.accent,
                border: `1px solid ${C.accent}66`, borderRadius: 8, padding: '9px 14px',
                fontSize: 13, fontWeight: 600 }}>
              ⬇ Baixar tudo (.md)
            </button>
          )}
          <button onClick={doGenerate} disabled={generate.isPending || selected.size === 0}
            style={{ cursor: generate.isPending ? 'default' : 'pointer',
              background: C.accent, color: C.black, border: 'none', borderRadius: 8,
              padding: '9px 16px', fontSize: 13, fontWeight: 700, opacity: generate.isPending ? 0.6 : 1 }}>
            {generate.isPending ? 'Gerando artefatos…' : artifacts.length ? '↻ Regerar Artefatos Selecionados' : 'Gerar Artefatos Selecionados'}
          </button>
        </div>
      </div>
      <p style={{ color: C.dim, maxWidth: '70ch', marginTop: 10, fontSize: 13.5 }}>
        Selecione os artefatos ({grupos.length} grupos · {catalogo.length} itens) e o
        cloud-architect (via AI Core/Claude) os gera a partir do Blueprint.
        Alimentam o Gate de Tecnologia.
      </p>

      {/* Catálogo selecionável — vem da API, agrupado por kind */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: 12, marginTop: 14 }}>
        {grupos.map((g) => (
          <div key={g.group} style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 14 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em',
              color: C.muted, marginBottom: 8 }}>{g.group}</div>
            {g.items.map((it) => (
              <label key={it.chave} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
                fontSize: 13, color: C.dim, cursor: it.obrigatorio ? 'not-allowed' : 'pointer' }}>
                <input type="checkbox" aria-label={it.titulo} checked={selected.has(it.chave)}
                  disabled={it.obrigatorio}
                  onChange={() => toggle(it.chave, it.obrigatorio)} />
                {it.titulo} {it.obrigatorio && <span style={{ fontSize: 10, color: C.muted }}>(obrigatório)</span>}
              </label>
            ))}
          </div>
        ))}
      </div>

      {generate.isError && (
        <div style={{ marginTop: 12, color: C.quantum, fontSize: 13 }}>
          Falha: {generate.error?.status === 409
            ? 'gere o Blueprint antes de gerar os artefatos.'
            : String(generate.error?.message || generate.error)}
        </div>
      )}
      {isLoading && <div style={{ marginTop: 20, color: C.dim }}>Carregando…</div>}
      {!isLoading && artifacts.length === 0 && (
        <div style={{ marginTop: 24, padding: 28, border: `1px dashed ${C.border}`, borderRadius: 12,
          color: C.dim, textAlign: 'center' }}>
          Sem artefatos ainda. Gere o <strong style={{ color: C.text }}>Blueprint</strong> e clique em
          {' '}<strong style={{ color: C.text }}>Gerar Artefatos Selecionados</strong>.
        </div>
      )}
      <AiTruncationNotice complete={data?.aiComplete} onRetry={doGenerate} />
      <div style={{ marginTop: 12 }}>
        {artifacts.map((a) => <Artifact key={a.id} a={a} />)}
      </div>
    </div>
  );
}
